import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { userPreferencesApi } from '../services/userPreferencesApi';

interface UserPreferencesContextValue {
  securityAccessibilityMode: boolean;
  isLoading: boolean;
  isSaving: boolean;
  /** False until the current account's server preference has been loaded successfully. */
  isReady: boolean;
  error: string | null;
  setSecurityAccessibilityMode: (enabled: boolean) => Promise<void>;
  retry: () => Promise<void>;
}

const UserPreferencesContext = createContext<UserPreferencesContextValue | undefined>(undefined);

function AccountPreferencesProvider({ accountId, children }: { accountId: string | null; children: ReactNode }) {
  const [securityAccessibilityMode, setMode] = useState(false);
  const [isLoading, setLoading] = useState(accountId !== null);
  const [isSaving, setSaving] = useState(false);
  const [isReady, setReady] = useState(accountId === null);
  const [error, setError] = useState<string | null>(null);
  const request = useRef<AbortController | null>(null);
  const desiredMode = useRef(false);

  const load = useCallback(async () => {
    if (!accountId || request.current) return;
    const controller = new AbortController();
    request.current = controller;
    setLoading(true);
    setError(null);
    try {
      const preferences = await userPreferencesApi.get(controller.signal);
      if (request.current !== controller || controller.signal.aborted) return;
      desiredMode.current = preferences.security_accessibility_mode;
      setMode(preferences.security_accessibility_mode);
      setReady(true);
    } catch (failure) {
      if (request.current !== controller || controller.signal.aborted) return;
      setError(failure instanceof Error ? failure.message : 'Your account preferences could not be loaded. Please retry.');
    } finally {
      if (request.current === controller) {
        request.current = null;
        setLoading(false);
      }
    }
  }, [accountId]);

  useEffect(() => {
    void load();
    return () => {
      request.current?.abort();
      request.current = null;
    };
  }, [load]);

  const setSecurityAccessibilityMode = useCallback(async (enabled: boolean) => {
    // Allow one active save at a time so rapid toggles do not send competing writes.
    if (!accountId || !isReady || request.current) return;
    const controller = new AbortController();
    request.current = controller;
    desiredMode.current = enabled;
    setMode(enabled);
    setSaving(true);
    setError(null);
    try {
      await userPreferencesApi.setSecurityAccessibilityMode(enabled, controller.signal);
    } catch (failure) {
      if (request.current !== controller || controller.signal.aborted) return;
      // Keep the selected pattern in this session, even if persistence failed. Never restore dots
      // unexpectedly for someone who has just enabled the accessibility mode.
      setError(failure instanceof Error ? failure.message : 'Your preference could not be saved. Please retry.');
    } finally {
      if (request.current === controller) {
        request.current = null;
        setSaving(false);
      }
    }
  }, [accountId, isReady]);

  const retry = useCallback(() => isReady
    ? setSecurityAccessibilityMode(desiredMode.current)
    : load(), [isReady, setSecurityAccessibilityMode, load]);

  return (
    <UserPreferencesContext.Provider value={{ securityAccessibilityMode, isLoading, isSaving, isReady, error, setSecurityAccessibilityMode, retry }}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  const { identity } = useAuth();
  const accountId = identity ? JSON.stringify([identity.user_uuid, identity.email, identity.raw?.custom?.sub]) : null;
  // Remount all preference state before an account change can paint the previous account's mode.
  // No browser storage is used; the server remains the source of truth on every sign-in/reload.
  return <AccountPreferencesProvider key={accountId ?? 'anonymous'} accountId={accountId}>{children}</AccountPreferencesProvider>;
}

export function useUserPreferences(): UserPreferencesContextValue {
  const context = useContext(UserPreferencesContext);
  if (!context) throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  return context;
}
